
import { BaseSyntheticEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import {ReactComponent as DeleteIcon} from '../../../images/nde-icon-delete.svg';
import { useDispatch, useSelector } from 'react-redux';
import { selectControls } from '../../../redux/controls';
import Spinner from '../../Spinner/Spinner';
import { TextFormControl } from '../../TextFormControl/TextFormControl';
import './EditRosterModal.scss';
import { EditRosterModalProps, RosterExpandable, RosterRequest, StudentInfo } from '../models';
import { cloneDeep, isEmpty } from 'lodash';
import { selectMaxTestTakersPerCustomTest } from '../../../redux/appSettings';
import { fetchRosterStudentsStart, selectRosterList, selectRostersLoaded, selectRostersLoading, updateRosterStart } from '../../../redux/rosters';

export const EditRosterModal = ({ open, onClose, rosterToEdit: roster }: EditRosterModalProps): JSX.Element => {

  const dispatch = useDispatch();

  const controls = useSelector(selectControls);
  const maxTestTakersPerCustomTest = useSelector(selectMaxTestTakersPerCustomTest);
  const isLoading = useSelector(selectRostersLoading);
  const isLoaded = useSelector(selectRostersLoaded);
  const rosterList = useSelector(selectRosterList);

  ReactModal.setAppElement('#root');

  const createRosterForm = useRef<HTMLFormElement>(null);
  const [rosterToEdit, setRosterToEdit] = useState<RosterExpandable|null>(roster);
  const [studentInfoList, setStudentInfoList] = useState<StudentInfo[]>([]);

  const [isValid, setIsValid] = useState<boolean>(false);

  const submitEditRosterHandler = useCallback((event: BaseSyntheticEvent, formEl: React.RefObject<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (formEl.current) {
      const formData = new FormData(formEl.current);
      const formProps = Object.fromEntries(formData);
      const rosterName = formProps['roster-name'] as string;
      const numStudents = formProps['num-of-students'] as string;

      dispatch( updateRosterStart({
        rosterName,
        numStudents: !numStudents ? '0' : numStudents,
        rosterID: rosterToEdit?.rosterID
      }));
      onClose({} as RosterRequest);

    }
  }, [dispatch, onClose, rosterToEdit?.rosterID]);

  const closeModalHandler = useCallback(() => {
    setIsValid(false);
    onClose({} as RosterRequest);
  }, [onClose]);

  useLayoutEffect(() => {
    if (!isEmpty(roster)) {
      setRosterToEdit(cloneDeep(roster));
      if (!roster.children || !roster.children?.length) {
        dispatch(
          fetchRosterStudentsStart({
            rosterName: roster.rosterName,
            rosterID: roster.rosterID,
            numStudents: '0'
          })
        );
        setStudentInfoList([]);
      } else {
        setStudentInfoList(roster.children);
      }
    } else {
      setRosterToEdit(null);
    }
  }, [dispatch, roster]);

  useEffect(() => {
    if (studentInfoList.length === 0 && isLoaded) {
      const foundRoster = rosterList.find(rts => rts.rosterID == roster.rosterID);
      if (foundRoster && foundRoster.children) {
        setStudentInfoList(foundRoster.children);
      }
    }
  }, [isLoaded, roster.rosterID, rosterList, studentInfoList.length]);

  return (
    <>
      {
        open &&
        <ReactModal
          isOpen={open}
          className="nqt-modal"
          overlayClassName="nqt-modal-overlay"
          shouldFocusAfterRender={true}
          contentLabel={controls?.RosterStudentRoster?.controlText}
        >
          <form ref={createRosterForm} onSubmit={(e) => submitEditRosterHandler(e, createRosterForm)} className='nqt-modal--container'>
            { isLoading && <Spinner/> }
            <div className="nqt-modal--header-group">
              <header className='nqt-modal--title'>{controls?.RosterStudentRoster?.controlText}</header>
              <button className='nqt-modal--close-button'
                type='button'
                onClick={() => closeModalHandler()}>
                <DeleteIcon/>
                <span className="visually-hidden">{controls?.ModalClose?.controlText}</span>
              </button>
            </div>
            <div className="nqt-modal--border">
              <section className="nqt-acc-modal--content container">
                <div className="nqt-modal--form-controls container-fluid">
                  <div className="row row-cols-1 gy-3">
                    <div className="col container-fluid">
                      <TextFormControl id='edit-roster--name'
                        containerClass="nqt-modal--text-input"
                        controlName='roster-name'
                        controlType="text"
                        defaultValue={rosterToEdit ? rosterToEdit.rosterName : undefined }
                        headerText={controls?.RosterName.controlText}
                        isValidCallback={() => setIsValid(!!createRosterForm.current?.checkValidity())}
                        pattern="^[A-Za-z 0-9_]{1,20}$"
                        required={true}
                        setFocus={true}/>
                    </div>
                    <div className="col container-fluid">
                      <TextFormControl id='edit-roster--num-of-students'
                        containerClass="nqt-modal--text-input number"
                        controlName='num-of-students'
                        controlType="number"
                        min={0}
                        max={maxTestTakersPerCustomTest}
                        maxLength={3}
                        headerText={controls?.RosterNumStudents?.controlText}
                        isValidCallback={() => setIsValid(!!createRosterForm.current?.checkValidity())}
                        required={false}/>
                    </div>
                  </div>
                  {
                    studentInfoList && !!studentInfoList.length &&
                  <div className="nqt-edit-roster-modal--student-list">
                    <>
                      <header>
                        <h3 className="nqt-edit-roster-modal--student-list__header">Student List</h3>
                      </header>
                      <div className="nqt-edit-roster-modal--student-list-container">
                        {
                          studentInfoList.map(studInfo => (
                            <li key={studInfo.studentID} className="nqt-edit-roster-modal--student-list-item">
                              <span className="nqt-edit-roster-modal--student-name">
                                {studInfo.studentName}
                              </span>
                            </li>
                          ))
                        }

                      </div>
                    </>
                  </div>
                  }
                </div>
              </section>
              <hr />
              <div className="nqt-modal--buttons col-9 offset-3">
                <button className="nqt-button--cancel col-4 offset-3"
                  type='button'
                  onClick={() => closeModalHandler()}>{controls?.ModalCancel?.controlText}</button>
                <button className="nqt-button--submit col-4"
                  disabled={!isValid}
                  type="submit"
                  onClick={(e) => submitEditRosterHandler(e, createRosterForm)}>{controls?.RosterSave?.controlText}</button>
              </div>
            </div>
          </form>
        </ReactModal>
      }
    </>
  );
};

export default EditRosterModal;